// ============================================================
// FORECAST CONSTANTS
// Static configuration data for the forecast pipeline.
//
// Includes:
//   - Non-paid evolution models (growth/decay per source type)
//   - Source-to-evolution matching logic
//   - Marketing platform identifiers
//   - Chart color palettes
// ============================================================

import { EvolutionConfig } from './types';

// ============================================================
// NON-PAID REVENUE EVOLUTION FUNCTIONS
// Research-backed evolution modifiers per source type.
// Each returns a multiplier ≥ 0 for day `t` into the future.
// ============================================================

export const NON_PAID_EVOLUTION: Record<string, EvolutionConfig> = {
    // Facebook organic: exponential decay, ~18%/year, floor at 70%
    'facebook': {
        fn: (t) => 0.70 + 0.30 * Math.exp(-0.0005 * t),
        label: 'Organic Facebook',
        color: '#3b82f6'
    },
    // Instagram organic: steeper decay, ~25%/year, floor at 60%
    'instagram': {
        fn: (t) => 0.60 + 0.40 * Math.exp(-0.0007 * t),
        label: 'Organic Instagram',
        color: '#e11d48'
    },
    // SEO / Organic Search: slow log growth, ~4%/year
    'seo': {
        fn: (t) => 1 + 0.04 * Math.log(1 + t / 90),
        label: 'SEO / Organic Search',
        color: '#16a34a'
    },
    'google': {
        fn: (t) => 1 + 0.04 * Math.log(1 + t / 90),
        label: 'Google Organic',
        color: '#f59e0b'
    },
    // Email marketing: linear growth, ~11%/year
    'email': {
        fn: (t) => 1 + 0.0003 * t,
        label: 'Email Marketing',
        color: '#8b5cf6'
    },
    // TikTok organic: decelerating growth, ~8%/year
    'tiktok': {
        fn: (t) => 1 + 0.10 * (1 - Math.exp(-t / 180)),
        label: 'Organic TikTok',
        color: '#ec4899'
    },
    // Direct traffic: near-flat, ~7%/year
    'direct': {
        fn: (t) => 1 + 0.0002 * t,
        label: 'Direct Traffic',
        color: '#0ea5e9'
    },
    // Referral: moderate linear growth, ~5.5%/year
    'referral': {
        fn: (t) => 1 + 0.00015 * t,
        label: 'Referral Traffic',
        color: '#14b8a6'
    },
    // Pinterest organic: moderate growth, ~8%/year
    'pinterest': {
        fn: (t) => 1 + 0.00022 * t,
        label: 'Organic Pinterest',
        color: '#ef4444'
    },
    // Snapchat organic: slight decay, ~10%/year, floor at 75%
    'snapchat': {
        fn: (t) => 0.75 + 0.25 * Math.exp(-0.0003 * t),
        label: 'Organic Snapchat',
        color: '#eab308'
    },
    // X/Twitter organic: decline, ~15%/year, floor at 65%
    'x': {
        fn: (t) => 0.65 + 0.35 * Math.exp(-0.00045 * t),
        label: 'Organic X',
        color: '#171717'
    },
    'twitter': {
        fn: (t) => 0.65 + 0.35 * Math.exp(-0.00045 * t),
        label: 'Organic X',
        color: '#171717'
    },
    // LinkedIn organic: moderate growth, ~6%/year
    'linkedin': {
        fn: (t) => 1 + 0.00016 * t,
        label: 'Organic LinkedIn',
        color: '#2563eb'
    },
    // Default: flat (no assumption)
    'default': {
        fn: (_t) => 1.0,
        label: 'Other',
        color: '#6b7280'
    }
};

// ============================================================
// SOURCE → EVOLUTION MATCHING
// Matches a given source name (case-insensitive) to its
// evolution config. Handles common aliases.
// ============================================================

/**
 * Match a source name to its evolution configuration.
 *
 * Lookup order:
 *  1. Direct key match (case-insensitive substring)
 *  2. Common aliases (meta → facebook, newsletter → email, etc.)
 *  3. Fallback to 'default' (flat evolution)
 */
export const getEvolutionConfig = (sourceName: string): EvolutionConfig => {
    const lower = sourceName.toLowerCase();
    for (const [key, config] of Object.entries(NON_PAID_EVOLUTION)) {
        if (key === 'default') continue;
        if (lower.includes(key)) return config;
    }
    // Check common aliases
    if (lower.includes('meta')) return NON_PAID_EVOLUTION['facebook'];
    if (lower.includes('organic') && lower.includes('search')) return NON_PAID_EVOLUTION['seo'];
    if (lower.includes('newsletter') || lower.includes('mail')) return NON_PAID_EVOLUTION['email'];
    return NON_PAID_EVOLUTION['default'];
};

// ============================================================
// MARKETING PLATFORM IDENTIFIERS
// Used to filter ad entities down to known marketing platforms.
// ============================================================

export const MARKETING_PLATFORMS = [
    'meta', 'facebook', 'instagram', 'tiktok', 'google', 'adwords',
    'snapchat', 'pinterest', 'x', 'linkedin', 'snap', 'bing', 'youtube'
];

// ============================================================
// CHART COLORS (per ROAS source)
// ============================================================

export const SOURCE_COLORS: Record<string, string> = {
    meta: '#3b82f6',
    tiktok: '#ec4899',
    google: '#f59e0b',
    snapchat: '#eab308',
    pinterest: '#ef4444',
    default: '#8b5cf6'
};

export const SOURCE_PALETTE = [
    '#10b981', // emerald-500
    '#3b82f6', // blue-500
    '#6366f1', // indigo-500
    '#f43f5e', // rose-500
    '#f59e0b', // amber-500
    '#8b5cf6', // violet-500
    '#ec4899', // pink-500
    '#06b6d4', // cyan-500
];

